/**
 * Energy-cert codec — pure `decodeEnergy(dto)→viewModel` / `encodeEnergy(vm)→dto`
 * pair with the FIXED-POINT property `encodeEnergy(decodeEnergy(dto)) === dto`
 * (JSON-normalized). MODULE-CONTRACT §2 + WAVE2-DECISIONS D-W2-11 (C1–C6).
 *
 * A faithful port of the legacy `getEnergyCertificateRequestData` (encode) +
 * `applyDto`/`buildWallType`/`buildVentilationAndHeatingMap` (decode), minus the
 * constate reads. The load-bearing correctness pieces:
 *
 *   - C4 (RETIRED 2026-08-31) — the insulation fields map STRAIGHT in both
 *     directions. Legacy crossed them on encode
 *     (`EnergyCertificateStep.context.ts:233-234`) and this codec reproduced the
 *     crossing symmetrically, so the round-trip closed and nothing looked wrong
 *     from inside the app — while the value typed under "roof" reached the wire
 *     as `subsequentBasementInsulation` and vice versa.
 *
 *     That crossing was a defect, not an adapter for a downstream quirk:
 *     platform maps the two fields name-for-name into Moovin's own DTO
 *     (`MoovinOrderMapper.kt:142-143` → `CreateDemandCertificateOrderDTO`), and
 *     that DTO carries no `@JsonProperty` alias on either field — unlike its
 *     siblings where Moovin's names differ (`heatedBasement`,
 *     `basementFloorEquipment`, `Photovoltaik`). Nothing between here and Moovin
 *     renames or re-crosses them. The crossing was introduced with the original
 *     implementation (`f8a9ac836`, BKBN-1704, 2024-12-03) inside an otherwise
 *     straight name-for-name mapping block, with no comment.
 *
 *     `PF_VERSION` is deliberately NOT bumped: PF v2 is still in development, so
 *     the only drafts carrying the crossed shape belong to internal testers. Such
 *     a draft resumes with the two values swapped — clear it and start fresh
 *     rather than trusting what it shows.
 *   - Whole-heater-array `.some()` handover flag — `encodeHeaters` takes the WHOLE
 *     array (editing one heater's fuel changes the encode of ALL heaters). C1: heater
 *     edits re-encode the `heaters` array, never the whole DTO.
 *   - Ventilation Set membership (`VENTILATION_SYSTEM_AVAILABLE`) is NOT on the wire →
 *     inferred on decode from `airDistribution/ventilationType/fanType` presence.
 * It never affects the encode output, so the round-trip closes.
 *   - PV null-branch, `parseInt` vs `toNumber`, string years, positional wall re-key.
 *
 * The pure `encode*` fns are (a) the test oracle and (b) how the field-granular
 * setters compute their owned/cross-derived wire fields (C1). This file is pure
 * (no React/store).
 */

import {
  BasementType,
  BuildingType,
  ConstructionType,
  GeneratedByType,
  GlobalShapeType,
  HeatingCombinationType,
  OrientationType,
  PhotovoltaicSystemType,
  VentilationSystemType,
  Wall,
  handoverAndPumpingFields
} from 'constants/purchaseFlow'
import type {
  BasementCeilingType,
  BuildingLocationType,
  DistributionType,
  FansType,
  HeatRecoveryPercentageTypes,
  LightweightConstructionType,
  RoofConditionType,
  RoofPitchType,
  SolidConstructionType,
  ThermalBridgeType,
  VentilationType,
  WindowType,
  WoodPelletsEnergySourceType
} from 'constants/purchaseFlow'
import { EnergyPassReason } from 'constants/purchaseFlow/sharedEnergyCertificateTypes'
import type { Nullable } from 'models/helpers'
import type {
  EnergyCertificateDetailsDTO,
  HeatingSystemItemDTO,
  HeatingSystemItemPayloaDTO,
  PhotovoltaicFeaturesPayloadDTO,
  WallPayloadDTO
} from 'models/purchaseFlow/energyCertificateOfNeedDTO'
import { toNumber } from 'utils/formatting/numberUtils'

import { DEFAULT_HEATER, hasVentilationSystem, type EnergyCertificateConfigDTO } from './energyCertificateOfNeed.dto'
import { emptyWall, recomputeWalls, type EnergyWalls } from './energyCertificateOfNeed.walls'

// ─── View-model types ─────────────────────────────────────────────────────────

export interface EnergyPhotovoltaicView {
  areaOfFacility: string
  peakPower: string
  orientationType: OrientationType
}

export interface EnergyVentilationView {
  ventilationSystem: Set<VentilationSystemType>
  distribution: Nullable<DistributionType>
  ventilation: Nullable<VentilationType>
  fans: Nullable<FansType>
  heatRecovery: Nullable<HeatRecoveryPercentageTypes>
}

/** The editable view derived from the DTO. Numeric-string fields keep string
 *  form for mid-edit fidelity; `heaters` is a plain array (stable ids live in
 *  the form controller, NOT here — dropped by encode, C6). */
export interface EnergyViewModel {
  // Property information
  buildingType: Nullable<BuildingType>
  buildingLocationType: Nullable<BuildingLocationType>
  residentialUnit: number
  commercialArea: string
  commercialAreaType: string
  constructionYear: string
  livingSpace: string
  // Building layout
  globalShapeType: GlobalShapeType
  orientationType: OrientationType
  walls: EnergyWalls
  // Construction method
  constructionType: ConstructionType
  lightweightConstructionType: Nullable<LightweightConstructionType>
  solidConstructionType: Nullable<SolidConstructionType>
  roofConditionType: Nullable<RoofConditionType>
  basementType: Nullable<BasementType>
  basementCeilingType: Nullable<BasementCeilingType>
  windowType: Nullable<WindowType>
  thermalBridgeType: ThermalBridgeType
  subsequentRoofInsulation: Nullable<number>
  subsequentBasementInsulation: Nullable<number>
  soilDepth: string
  heatedBasementArea: Nullable<number>
  roofPitchType: RoofPitchType
  numberOfFullFloors: Nullable<number>
  floorHeight: string
  yearOfManufacture: Nullable<number>
  // Ventilation and heating
  heaters: HeatingSystemItemDTO[]
  photovoltaicSystem: PhotovoltaicSystemType
  photovoltaicFeatures: EnergyPhotovoltaicView
  ventilationFeatures: EnergyVentilationView
  // Additional information
  reasonForEnergyPass: EnergyPassReason
  messageToExpert: string
}

const isBasementHeated = (basement: EnergyViewModel['basementType']): boolean =>
  basement === BasementType.HEATED_WINDOW_SOLAR_GAIN || basement === BasementType.HEATED_NO_WINDOW

// ─── Walls ─────────────────────────────────────────────────────────────────────

/** Positional wire array (A..H by index) → view walls. When the array is empty
 *  (fresh/default), derive the shape-pruned empty wall set (collection-default). */
export const decodeWalls = (shape: GlobalShapeType, dims: WallPayloadDTO[] | undefined): EnergyWalls => {
  const dimensions = dims ?? []
  if (!dimensions.length) return recomputeWalls(shape, allWalls())

  const wallKeys = Object.values(Wall)
  const out: EnergyWalls = {}
  dimensions.forEach((dim, index) => {
    const key = wallKeys[index]
    if (!key) return
    out[key] = {
      mounted: !!dim.subsequentlyBuilt,
      insulated: !!dim.subsequentlyInsulated,
      length: dim.length != null ? String(dim.length) : '',
      // An empty insulation is saved as 0, which is no value on a wall that was not insulated.
      insulation: dim.wallInsulation != null && (dim.subsequentlyInsulated || dim.wallInsulation !== 0) ? String(dim.wallInsulation) : '',
    }
  })
  return out
}

/** View walls → positional wire array (`Object.values` insertion order). Empty
 *  insulation → `toNumber('')===0` (Number('')===0, NOT NaN) — reproduced. */
export const encodeWalls = (walls: EnergyWalls): WallPayloadDTO[] =>
  Object.values(walls).map((w) => ({
    length: toNumber(w.length),
    subsequentlyBuilt: w.mounted,
    subsequentlyInsulated: w.insulated,
    wallInsulation: toNumber(w.insulation),
  }))

/** All 8 walls, fresh/empty — the pre-prune base for the collection-default. */
const allWalls = (): EnergyWalls =>
  Object.values(Wall).reduce<EnergyWalls>((acc, w) => {
    acc[w] = emptyWall()
    return acc
  }, {})

// ─── Heaters (whole-array) ──────────────────────────────────────────────────────

export const decodeHeater = (h: HeatingSystemItemPayloaDTO): HeatingSystemItemDTO => {
  const combined = new Set<HeatingCombinationType>()
  if (h.hasCentralHotWaterGeneration) combined.add(HeatingCombinationType.CENTRAL_HOT_WATER_GENERATION)
  if (h.solarThermalHeatingSupport) combined.add(HeatingCombinationType.SOLAR_THERMAL_ENERGY_FOR_HEATING_SUPPORT)
  if (h.solarThermalHotWaterSupport) combined.add(HeatingCombinationType.SOLAR_THERMAL_ENERGY_FOR_HOT_WATER_SUPPORT)

  return {
    isHydraulicBalancing: !!h.isHydraulicBalancingDone,
    heatingFuel: h.heatingType ?? null,
    heatingFuelOption: h.heatingSubType ?? null,
    woodPalletsEnergySource: (h.energySource as WoodPelletsEnergySourceType) ?? null,
    heatingDesignation: typeof h.typeDesignation === 'string' ? h.typeDesignation : String(h.typeDesignation ?? ''),
    heaterLocation: h.location ?? null,
    waterLocation: h.centralHotWater?.location ?? null,
    nightService: h.nightSwitch ?? null,
    heatTransferType: h.heatTransfer ?? null,
    heatingPipeType: h.heatingPipe ?? null,
    circulatingPumpType: h.circulatingPump ?? null,
    generatedBy: h.decentralizedHotWater?.energySource ?? GeneratedByType.ELECTRIC,
    generatorYearOfManufacture: h.yearOfConstruction ?? null,
    heatingPower: h.powerKw ?? null,
    yearOfConstruction: h.decentralizedHotWater?.constructionYear ?? h.centralHotWater?.constructionYear ?? null,
    decentralizedHeatingType: h.decentralizedHotWater?.type ?? null,
    totalHeatingOutput: h.shareInTotalHeatingPower ?? null,
    heatingCombinedWith: combined,
    isHotWaterCirculation: !!h.centralHotWater?.hotWaterCirculation,
  }
}

/** Empty/absent wire heaters → the single collection-default heater view. */
export const decodeHeaters = (heaters: HeatingSystemItemPayloaDTO[] | undefined): HeatingSystemItemDTO[] => {
  if (!heaters || !heaters.length) return [{ ...DEFAULT_HEATER, heatingCombinedWith: new Set() }]
  return heaters.map(decodeHeater)
}

/**
 * View heaters → wire heaters. WHOLE-ARRAY: `sendHandover` is computed across ALL
 * heaters (any handover fuel ⇒ every heater emits the 3 handover fields), so this
 * MUST take the full array — a single heater's fuel edit re-encodes them all (C1).
 */
export const encodeHeaters = (heaters: HeatingSystemItemDTO[]): HeatingSystemItemPayloaDTO[] => {
  const sendHandover = heaters.some((h) => (h.heatingFuel ? handoverAndPumpingFields.has(h.heatingFuel) : false))

  return heaters.map((h) => {
    const isCentral = h.heatingCombinedWith.has(HeatingCombinationType.CENTRAL_HOT_WATER_GENERATION)
    return {
      yearOfConstruction: h.generatorYearOfManufacture,
      location: h.heaterLocation,
      nightSwitch: h.nightService,
      typeDesignation: h.heatingDesignation,
      heatingType: h.heatingFuel,
      heatingSubType: h.heatingFuelOption,
      energySource: h.woodPalletsEnergySource,
      heatTransfer: sendHandover ? h.heatTransferType : null,
      heatingPipe: sendHandover ? h.heatingPipeType : null,
      circulatingPump: sendHandover ? h.circulatingPumpType : null,
      decentralizedHotWater: !isCentral
        ? { energySource: h.generatedBy, constructionYear: h.yearOfConstruction, type: h.decentralizedHeatingType }
        : undefined,
      centralHotWater: isCentral
        ? { location: h.waterLocation, constructionYear: h.yearOfConstruction, hotWaterCirculation: h.isHotWaterCirculation }
        : undefined,
      solarThermalHeatingSupport: h.heatingCombinedWith.has(HeatingCombinationType.SOLAR_THERMAL_ENERGY_FOR_HEATING_SUPPORT),
      solarThermalHotWaterSupport: h.heatingCombinedWith.has(HeatingCombinationType.SOLAR_THERMAL_ENERGY_FOR_HOT_WATER_SUPPORT),
      isHydraulicBalancingDone: h.isHydraulicBalancing,
      powerKw: h.heatingPower || 0,
      shareInTotalHeatingPower: h.totalHeatingOutput || 0,
      hasCentralHotWaterGeneration: isCentral,
    }
  })
}

// ─── Photovoltaics ──────────────────────────────────────────────────────────────

export const decodePhotovoltaics = (
  pv: EnergyCertificateConfigDTO['photovoltaics']
): { photovoltaicSystem: PhotovoltaicSystemType; photovoltaicFeatures: EnergyPhotovoltaicView } => {
  if (pv == null) {
    return {
      photovoltaicSystem: PhotovoltaicSystemType.NO,
      photovoltaicFeatures: { areaOfFacility: '', peakPower: '', orientationType: OrientationType.NORTH },
    }
  }
  return {
    photovoltaicSystem: PhotovoltaicSystemType.YES,
    photovoltaicFeatures: {
      areaOfFacility: pv.area != null ? String(pv.area) : '',
      peakPower: pv.peakPower != null ? String(pv.peakPower) : '',
      orientationType: pv.geographicOrientation,
    },
  }
}

export const encodePhotovoltaics = (
  system: PhotovoltaicSystemType,
  features: EnergyPhotovoltaicView
): PhotovoltaicFeaturesPayloadDTO | null =>
  system === PhotovoltaicSystemType.YES
    ? { area: toNumber(features.areaOfFacility), peakPower: toNumber(features.peakPower), geographicOrientation: features.orientationType }
    : null

// ─── Ventilation ────────────────────────────────────────────────────────────────

export const decodeVentilation = (dto: EnergyCertificateConfigDTO): EnergyVentilationView => {
  const ventilationSystem = new Set<VentilationSystemType>()
  if (dto.hasWindowVentilation) ventilationSystem.add(VentilationSystemType.WINDOW_VENTILATION)
  // D-W2-4 / C5: the wire DTO does NOT store VENTILATION_SYSTEM_AVAILABLE membership. DF-18 added a
  // draft-only marker for it; `hasVentilationSystem` prefers that and falls back to field presence,
  // which is all a draft written before it carries.
  if (hasVentilationSystem(dto)) {
    ventilationSystem.add(VentilationSystemType.VENTILATION_SYSTEM_AVAILABLE)
  }
  return {
    ventilationSystem,
    distribution: dto.airDistribution ?? null,
    ventilation: dto.ventilationType ?? null,
    fans: dto.fanType ?? null,
    heatRecovery: dto.heatRecovery ?? null,
  }
}

type VentilationWireFields = Pick<
  EnergyCertificateConfigDTO,
  'hasWindowVentilation' | 'airDistribution' | 'ventilationType' | 'fanType' | 'heatRecovery' | 'hasVentilationSystem'
>

export const encodeVentilation = (v: EnergyVentilationView): VentilationWireFields => ({
  hasWindowVentilation: v.ventilationSystem.has(VentilationSystemType.WINDOW_VENTILATION),
  // Recorded rather than inferred, so ticking the box and filling nothing is incomplete
  // instead of indistinguishable from not ticking it. Stripped before the wire.
  hasVentilationSystem: v.ventilationSystem.has(VentilationSystemType.VENTILATION_SYSTEM_AVAILABLE),
  airDistribution: v.distribution,
  ventilationType: v.ventilation,
  fanType: v.fans,
  heatRecovery: v.heatRecovery,
})

// ─── Full DTO ↔ view codec ──────────────────────────────────────────────────────

export const decodeEnergy = (dto: EnergyCertificateConfigDTO): EnergyViewModel => {
  const shape = dto.buildingShape ?? GlobalShapeType.RECTANGLE
  const { photovoltaicSystem, photovoltaicFeatures } = decodePhotovoltaics(dto.photovoltaics)

  return {
    // Property information
    buildingType: dto.buildingType ?? null,
    buildingLocationType: dto.buildingLocation ?? null,
    residentialUnit: dto.livingUnits ?? 1,
    commercialArea: dto.commercialSpaceInSquareMeters ? String(dto.commercialSpaceInSquareMeters) : '',
    commercialAreaType: dto.typeOfUseCommercial ?? '',
    constructionYear: dto.buildingYear ?? '',
    livingSpace: dto.totalLivingArea ? String(dto.totalLivingArea) : '',
    // Building layout
    globalShapeType: shape,
    orientationType: dto.orientation ?? OrientationType.NORTH,
    walls: decodeWalls(shape, dto.wallDimensions),
    // Construction method
    constructionType: dto.constructionType ?? ConstructionType.MASSIVE_CONSTRUCTION,
    lightweightConstructionType:
      dto.constructionType === ConstructionType.LIGHTWEIGHT_CONSTRUCTION ? ((dto.wallType as LightweightConstructionType) ?? null) : null,
    solidConstructionType:
      dto.constructionType !== ConstructionType.LIGHTWEIGHT_CONSTRUCTION ? ((dto.wallType as SolidConstructionType) ?? null) : null,
    roofConditionType: dto.roofCondition ?? null,
    basementType: dto.basement ?? null,
    basementCeilingType: dto.basementCeilingType ?? null,
    windowType: dto.windowType ?? null,
    thermalBridgeType: dto.thermalBridges,
    // Straight, both directions — see the C4 note in the file header.
    subsequentRoofInsulation: dto.subsequentRoofInsulation ?? null,
    subsequentBasementInsulation: dto.subsequentBasementInsulation ?? null,
    soilDepth: dto.soilDepth != null ? String(dto.soilDepth) : '',
    heatedBasementArea: typeof dto.heatedBasementArea === 'number' ? dto.heatedBasementArea : null,
    roofPitchType: dto.roofPitch,
    numberOfFullFloors: dto.numberFullFloors ?? 1,
    floorHeight: dto.floorHeightMeters != null ? String(dto.floorHeightMeters) : '',
    yearOfManufacture: dto.windowConstructionYear ?? null,
    // Ventilation and heating
    heaters: decodeHeaters(dto.heaters),
    photovoltaicSystem,
    photovoltaicFeatures,
    ventilationFeatures: decodeVentilation(dto),
    // Additional information
    reasonForEnergyPass: dto.reasonForEnergyPass ?? EnergyPassReason.NEW_RENTAL,
    messageToExpert: dto.messageToExpert ?? '',
  }
}

/**
 * View → wire DTO (test oracle + cross-derived source for the field-granular
 * setters). Emits ONLY the wire fields (NOT the FE-only `*Filenames` markers —
 * those are owned by the upload sync and never round-trip through the codec).
 */
export const encodeEnergy = (vm: EnergyViewModel): EnergyCertificateDetailsDTO => {
  const heated = isBasementHeated(vm.basementType)
  return {
    // Property information
    buildingType: vm.buildingType,
    livingUnits: vm.residentialUnit,
    buildingLocation: vm.buildingLocationType,
    totalLivingArea: toNumber(vm.livingSpace),
    commercialSpaceInSquareMeters: vm.buildingType === BuildingType.MIXED_USE_BUILDING ? parseInt(vm.commercialArea) : 0,
    typeOfUseCommercial: vm.buildingType === BuildingType.MIXED_USE_BUILDING ? vm.commercialAreaType : '',
    // Building layout
    orientation: vm.orientationType,
    buildingYear: vm.constructionYear,
    buildingShape: vm.globalShapeType,
    wallDimensions: encodeWalls(vm.walls),
    // Construction method
    numberFullFloors: vm.numberOfFullFloors,
    roofCondition: vm.roofConditionType,
    floorHeightMeters: toNumber(vm.floorHeight),
    roofPitch: vm.roofPitchType,
    // Insulation SWAP reproduced byte-identical — decode crosses it back (C4).
    subsequentRoofInsulation: vm.subsequentRoofInsulation ?? 0,
    subsequentBasementInsulation: vm.subsequentBasementInsulation ?? 0,
    basement: vm.basementType,
    soilDepth: heated ? toNumber(vm.soilDepth) : null,
    heatedBasementArea: heated ? vm.heatedBasementArea : null,
    basementCeilingType: vm.basementCeilingType,
    windowConstructionYear: vm.yearOfManufacture,
    windowType: vm.windowType,
    constructionType: vm.constructionType,
    wallType: vm.constructionType === ConstructionType.LIGHTWEIGHT_CONSTRUCTION ? vm.lightweightConstructionType : vm.solidConstructionType,
    thermalBridges: vm.thermalBridgeType,
    // Energy and heating
    photovoltaics: encodePhotovoltaics(vm.photovoltaicSystem, vm.photovoltaicFeatures),
    ...encodeVentilation(vm.ventilationFeatures),
    heaters: encodeHeaters(vm.heaters),
    // Additional information
    reasonForEnergyPass: vm.reasonForEnergyPass,
    messageToExpert: vm.messageToExpert,
  }
}
